'use client';

import { c as _c } from "react/compiler-runtime";
import { isFeatureFlagForced } from './forced-feature-flags';
import { usePostHogFeatureFlag, usePostHogFeatureFlagResolved } from './providers/posthog-feature-flag-hook';
import type { FeatureFlagResolvedState } from './types/feature-flag-resolution';
import { featureFlagResolved } from './types/feature-flag-resolution';

/**
 * Client-side hook to check if a feature flag is enabled.
 *
 * To switch the underlying provider: update the import above to point
 * to a different hook in `./providers/`. No other files need to change.
 *
 * @param flagName - The feature flag name to check
 * @returns true if enabled, false otherwise (or while loading)
 */
export function useIsFeatureEnabled(flagName) {
  const $ = _c(3);
  const enabled = usePostHogFeatureFlag(flagName);
  let t0;
  if ($[0] !== enabled || $[1] !== flagName) {
    t0 = isFeatureFlagForced(flagName) || enabled;
    $[0] = enabled;
    $[1] = flagName;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/**
 * Tri-state variant: returns `{enabled, isResolved}` so callers that
 * initiate side effects (OAuth redirects, signIn.create, mutations)
 * can wait for the provider to settle before firing. `enabled` is
 * always `false` while `isResolved` is `false`.
 *
 * Use this when reading the flag races against a side effect. Pure
 * render gates should use the cheaper `useIsFeatureEnabled` boolean.
 */
export function useIsFeatureEnabledResolved(flagName, options) {
  const $ = _c(1);
  const resolved = usePostHogFeatureFlagResolved(flagName, options);
  if (isFeatureFlagForced(flagName)) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = featureFlagResolved(true, "resolved");
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  return resolved;
}